import { useEffect, useMemo, useRef, useState } from 'react'
import { EntfernenAbgelehnt, hasStoredFile, listDocs, removeUploadedDoc, type DocRow } from '@/core/data/dokumente'
import { currentVariant } from '@/core/data/session'
import { descriptionFor, gelisteteNamenFuer, type Aktenplan } from '@/variants'
import { useApp, useZurueckgezogenRow } from '@/core/state/appStore'
import { formatDatum } from '@/core/lib/dates'
import { docLabel, docTitle, docType } from '@/core/lib/docLabels'
import { bySheetName } from '@/core/lib/sheets'
import { ListShell } from '@/core/components/list/ListShell'
import { AskModal } from '@/core/components/ui/ask-modal'
import { ModalHeader } from '../ModalHeader'
import {
  Button,
  Icon,
  ListRowAction,
  Menu,
  MenuRow,
  ScrollBar,
  StatusLine,
  toastError,
  toastSuccess,
} from 'admination-react'

const KNOWN_TYPE_ORDER = [
  'erstgespraech',
  'avgs',
  'bestaetigung-empty',
  'bestaetigung',
  'bewilligung',
  'vertrag',
  'datenschutz',
  'nachweis',
]

function groupOrderFor(docs: DocRow[], gelistet: readonly string[]): string[] {
  const present = new Set(docs.map(docType))
  const known = KNOWN_TYPE_ORDER.filter((t) => present.has(t))
  const unknown = [...present]
    .filter((t) => !KNOWN_TYPE_ORDER.includes(t) && !gelistet.includes(t) && t !== 'teilnehmerakte')
    .sort()
  return [...gelistet, ...known, ...unknown, ...(present.has('teilnehmerakte') ? ['teilnehmerakte'] : [])]
}

const KEINE: readonly string[] = []

const OHNE_REGISTER = 'ohne'

const registerVon = (plan: Aktenplan) => (row: DocRow): string => {
  const nummer = row.sheet ? plan.teilnehmerakte_register : plan.register.find((r) => r.dokumente.includes(row.name))?.nummer
  return plan.register.some((r) => r.nummer === nummer) ? String(nummer) : OHNE_REGISTER
}

const platzIn = (plan: Aktenplan | null, row: DocRow): number => plan?.register.flatMap((r) => r.dokumente).indexOf(row.name) ?? 0

const anzahl = (n: number, testId?: string) => (
  <span className="list-group-count" data-testid={testId}>
    {n}
  </span>
)

const registerGruppen = (plan: Aktenplan, rows: readonly DocRow[]) =>
  [...plan.register.map((r) => ({ id: String(r.nummer), header: `${r.nummer} ${r.name}` })), { id: OHNE_REGISTER, header: 'Ohne Register' }].map(
    ({ id, header }) => ({
      id,
      header,
      trailing: anzahl(rows.filter((d) => registerVon(plan)(d) === id && hasStoredFile(d)).length, `dokumente-register-${id}`),
    }),
  )

const ohneZeile = (name: string): DocRow => ({ id: '', name, bucketAddress: '', fileType: '', sitzungDatum: '', sheet: false, createdAt: '' })

const neuesteZuerst = (a: DocRow, b: DocRow) => Date.parse(b.createdAt) - Date.parse(a.createdAt)

const abgegeben = (row: DocRow) => !!row.abgegeben

export function PersonalDokumente({ coachingId }: { coachingId: string | null }) {
  const scrollerRef = useRef<HTMLElement>(null)
  const [stored, setDocs] = useState<DocRow[]>([])
  const [gelistet, setGelistet] = useState<readonly string[]>(KEINE)
  const [aktenplan, setAktenplan] = useState<Aktenplan | null>(null)
  const leseAktenplan = descriptionFor(currentVariant()).aktenplan
  const { dispatch, coachings, reloadDashboard } = useApp()
  const hochladbar = descriptionFor(currentVariant()).hochladbareDokumente ?? KEINE

  const zurueckgezogenRow = useZurueckgezogenRow(coachingId)
  const zurueckgezogen = zurueckgezogenRow !== null
  const eingeschraenkt = (zurueckgezogenRow ?? coachings.find((c) => c.id === coachingId))?.eingeschraenkt === true
  const hochladen = hochladbar.length > 0 && !eingeschraenkt
  const [stand, setStand] = useState(0)
  const [entfernen, setEntfernen] = useState<DocRow | null>(null)
  const [entfernt, setEntfernt] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  const datiert = useMemo(() => new Set([...hochladbar, ...stored.filter(abgegeben).map((d) => d.name)]), [hochladbar, stored])
  const docs = useMemo(
    () => [
      ...[...hochladbar, ...gelistet].filter((name) => !stored.some((d) => d.name === name)).map(ohneZeile),
      ...stored.filter((d) => datiert.has(d.name)).sort(neuesteZuerst),
      ...stored.filter((d) => !datiert.has(d.name)),
    ],
    [hochladbar, gelistet, stored, datiert],
  )

  useEffect(() => {
    let cancelled = false
    setDocs([])
    setGelistet(KEINE)
    if (!coachingId) return
    void gelisteteNamenFuer(descriptionFor(currentVariant()).gelisteteDokumente, coachingId).then((namen) => {
      if (!cancelled) setGelistet(namen)
    })
    return () => {
      cancelled = true
    }
  }, [coachingId])

  useEffect(() => {
    let cancelled = false
    if (!coachingId) return
    listDocs(coachingId)
      .then((rows) => {
        if (!cancelled) setDocs(rows)
      })
      .catch((e) => console.error('Dokumente laden fehlgeschlagen:', e))
    return () => {
      cancelled = true
    }
  }, [coachingId, stand])

  useEffect(() => {
    let cancelled = false
    leseAktenplan?.()
      .then((plan) => {
        if (!cancelled) setAktenplan(plan)
      })
      .catch((e) => {
        console.error('Aktenplan laden fehlgeschlagen:', e)
        toastError('Der Aktenplan konnte nicht geladen werden.')
      })
    return () => {
      cancelled = true
    }
  }, [leseAktenplan])

  function hochladenOeffnen(name: string) {
    if (coachingId) dispatch({ type: 'SET_CONTROL', key: 'selectedCoachingId', value: coachingId })
    dispatch({ type: 'CHANGE_PHASE', target: { modal: 'dokument-hochladen', tab: 'form', returnTo: 'documents', document: { name, id: null } } })
  }

  function open(row: DocRow) {
    if (!hasStoredFile(row) && hochladen && hochladbar.includes(row.name)) return hochladenOeffnen(row.name)
    if (!hasStoredFile(row) && (zurueckgezogen || !!row.ersetztAm || !gelistet.includes(row.name))) return
    if (coachingId) dispatch({ type: 'SET_CONTROL', key: 'selectedCoachingId', value: coachingId })
    dispatch({ type: 'CHANGE_PHASE', target: { modal: 'dokument', tab: 'form', returnTo: 'documents', document: { name: row.name, id: row.id } } })
  }

  const rows = useMemo(
    () => [
      ...docs.filter((d) => !d.sheet).sort((a, b) => platzIn(aktenplan, a) - platzIn(aktenplan, b)),
      ...docs.filter((d) => d.sheet).sort((a, b) => bySheetName(a.name, b.name)),
    ],
    [docs, aktenplan],
  )

  const groups = useMemo(
    () =>
      groupOrderFor(docs, gelistet).map((type) => ({
        id: type,
        header: docLabel(type),
        trailing: anzahl(docs.filter((d) => docType(d) === type && hasStoredFile(d)).length, `dokumente-gruppe-${type}`),
      })),
    [docs, gelistet],
  )

  const title = (row: DocRow) => (datiert.has(row.name) && row.id ? `${docTitle(row)} ${formatDatum(row.createdAt)}` : docTitle(row))

  const entfernbar = (row: DocRow) => hochladbar.includes(row.name) && !!row.id && hasStoredFile(row) && !row.signiert

  function schliessen() {
    setEntfernen(null)
    setFehler(null)
  }

  async function entfernenBestaetigt(row: DocRow) {
    setEntfernt(true)
    try {
      await removeUploadedDoc(row.id)
      toastSuccess(`${title(row)} entfernt`)
      schliessen()
    } catch (e) {
      console.error('Datei entfernen fehlgeschlagen:', e)
      if (!(e instanceof EntfernenAbgelehnt)) return setFehler('Die Datei konnte nicht entfernt werden.')
      if (!e.weg) return setFehler(e.message)
      toastError(e.message)
      schliessen()
    } finally {
      setEntfernt(false)
      setStand((n) => n + 1)
      reloadDashboard()
    }
  }

  return (
    <>
      <ModalHeader onClose={() => dispatch({ type: 'CLOSE' })}>
        <div className="pill-cluster">
          {hochladen && (
            <Menu
              className="pill"
              isList
              trigger={({ open: offen, triggerProps }) => (
                <Button {...triggerProps} isActive={offen} data-testid="dokumente-hochladen">
                  <Icon name="i-upload" />
                  Dokumente hochladen
                </Button>
              )}
            >
              <div data-testid="dokumente-hochladen-menue">
                {hochladbar.map((name) => (
                  <MenuRow key={name} label={name} data-testid={`dokumente-hochladen-${name}`} onSelect={() => hochladenOeffnen(name)} />
                ))}
              </div>
            </Menu>
          )}

        </div>
      </ModalHeader>
      <main className="form" data-scroll-host ref={scrollerRef}>
        {(!leseAktenplan || aktenplan) && (
          <ListShell
            rows={rows}
            title={title}
            properties={[
              {
                id: 'status',
                value: (row) =>
                  row.ersetztAm
                    ? `Ersetzt am ${formatDatum(row.ersetztAm)}`
                    : abgegeben(row)
                      ? 'Ohne Unterschrift der teilnehmenden Person abgegeben'
                      : datiert.has(row.name) && row.signiert
                        ? 'Nachträglich unterschrieben'
                        : hasStoredFile(row)
                      ? 'Datei vorhanden'
                      : 'Keine Datei hinterlegt',
              },
            ]}
            rowKey={(row) => row.id || row.name}
            rowTestId={(row) => (hochladbar.includes(row.name) ? `dokumente-datei-${row.name}-${row.id || 'keine'}` : undefined)}
            onRowClick={open}
            rowAction={
              hochladen
                ? (row) =>
                    entfernbar(row) ? (
                      <ListRowAction
                        icon="i-trash"
                        label="Entfernen"
                        data-testid={`dokumente-entfernen-${row.name}-${row.id}`}
                        onClick={(e) => {
                          e.stopPropagation()
                          setEntfernen(row)
                        }}
                      />
                    ) : null
                : undefined
            }
            groupBy={aktenplan ? registerVon(aktenplan) : docType}
            groups={aktenplan ? registerGruppen(aktenplan, rows) : groups}
            emptyText="Keine Dokumente vorhanden"
          />
        )}
      </main>
      <ScrollBar scrollerRef={scrollerRef} />
      <AskModal
        open={entfernen !== null}
        icon="i-trash"
        iconStroke
        variant="is-error"
        question={entfernen ? `Datei „${title(entfernen)}“ aus „${entfernen.name}“ entfernen?` : ''}
        description={
          fehler ? (
            <StatusLine as="span" tone="error" role="alert" data-testid="dokument-entfernen-fehler">
              {fehler}
            </StatusLine>
          ) : (
            'Das kann nicht rückgängig gemacht werden.'
          )
        }
        confirmLabel="Entfernen"
        cancelLabel="Abbrechen"
        cancelButton
        busy={entfernt}
        onConfirm={() => entfernen && void entfernenBestaetigt(entfernen)}
        onCancel={schliessen}
      />
    </>
  )
}
